import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { useLazyGetFactoryPrintReceivePreviewQuery } from '@/store/api/factoryApi'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: form_frmFactoryReceiveSubForm.txt → btnPrintRp_Click
// (`DoCmd.OpenReport "rpFactoryGrn", acViewReport`). Pattern same as Sale
// Contract Print-Off/Print-On (saleContract/PrintOffDialog.tsx,
// SaleAmendmentPrintDialog.tsx): BE trả HTML, FE preview trong iframe, button
// Print → `printAndUploadReport` để convert sang PDF + lưu archive + mở
// browser print dialog.

interface ReceivePrintDialogProps {
  open: boolean
  factoryCode: string
  grnId: string | null
  canEdit?: boolean
  onClose: () => void
}

// Template Phiếu nhập kho dùng Access-report mode: mỗi control là 1 absolute-
// positioned div (.ctl) với left/top/width tính từ twips. Override CSS preview
// để đảm bảo cũng ổn trong iframe scaled.
const PRINT_PREVIEW_CSS = `
@page { size: A4 !important; margin: 0 !important; }
@media print {
  @page { size: A4 !important; margin: 0 !important; }
  body { margin: 0 !important; }
  .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 9mm 11mm !important; }
}
@media screen and (max-width: 600px) {
  .report-page { min-height: auto !important; max-width: 100% !important; width: 100% !important; padding: 4mm !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; }
}
body { margin: 0 !important; }
.print-action { display: none !important; }
.report-page { width: 100% !important; max-width: 200mm !important; padding: 0 !important; min-height: auto !important; margin: 0 auto !important; }
.access-report { position: relative !important; margin: 0 auto !important; background: #fff !important; }
.ctl { position: absolute !important; overflow: visible !important; line-height: 1.2 !important; }
.ctl.bold { font-weight: 700 !important; }
.ctl.align-center { text-align: center !important; }
.ctl.align-right { text-align: right !important; }
.ctl img { width: 100% !important; height: 100% !important; object-fit: contain !important; object-position: left center !important; display: block !important; }
`.trim()

function buildPreviewHtml(rawHtml: string): string {
  const htmlWithoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = htmlWithoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="receive-print-preview-overrides">${PRINT_PREVIEW_CSS}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

export default function ReceivePrintDialog({
  open,
  factoryCode,
  grnId,
  canEdit = true,
  onClose,
}: ReceivePrintDialogProps) {
  const [previewing, setPreviewing] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [triggerPreview] = useLazyGetFactoryPrintReceivePreviewQuery()

  // Bám tiêu đề pattern "Print Preview - Offshore Contract" của PrintOffDialog.
  const previewTitle = grnId
    ? `Print Preview - Goods Receipt Note (${grnId})`
    : 'Print Preview - Goods Receipt Note'
  const iframeTitle = 'Receive Print Preview'

  useEffect(() => {
    if (!open || !grnId || previewing || previewHtml) return
    let cancelled = false
    const run = async () => {
      setPreviewing(true)
      try {
        const result = await triggerPreview({ factoryCode, grnId }).unwrap()
        if (cancelled) return
        setPreviewHtml(buildPreviewHtml(result.html))
        setToastMessage(result.message ?? 'Print preview generated.')
      } catch (err) {
        if (cancelled) return
        // BE Access-port guards (PROCESSING / Missing QC) trả 422 với detail
        // là user-facing message.
        const e = err as { status?: number; data?: { detail?: string } }
        if (e?.status === 422) {
          setToastMessage(e.data?.detail || 'Cannot print this record')
        } else if (e?.status === 404) {
          setToastMessage('GRN not found')
        } else {
          setToastMessage('Failed to generate print preview.')
        }
        onClose()
      } finally {
        if (!cancelled) setPreviewing(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, grnId, factoryCode])

  const handleClose = () => {
    setPreviewHtml('')
    onClose()
  }

  return (
    <>
      {previewing && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog open={Boolean(previewHtml)} onClose={handleClose} maxWidth={false} fullWidth PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={previewTitle}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Goods Receipt Note</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>GRN</Box>
            {grnId ? ` (${grnId})` : ''}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose} size="small">
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!canEdit}
              onClick={() => {
                const printTitle = `${sanitizePdfFileNamePart(grnId ?? 'receive')}_Factory_GRN.pdf`
                void printAndUploadReport({
                  iframeTitle,
                  reportName: 'rpFactoryGrn',
                  printTitle,
                  html: previewHtml,
                  onUploadError: setToastMessage,
                })
              }}
            >
              Print
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  )
}
